import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useEquipmentListStyles } from '../../../components/equipment/EquipmentList.styles';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { TApiEquipmentLocation, TEquipmentState } from '../../../types/Equipment';
import { IAppState } from '../../../store/Store';
import {
  filterEquipmentList,
  getEquipmentCategories,
  getEquipmentList,
} from '../../../actions/EquipmentActions';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import PageContent from '../../../components/PageContent';
import { ContentPage } from '../../../types/Content';
import EquipmentCategoryDropdown from '../../../components/equipment/EquipmentCategoryDropdown';
import { debounce } from 'lodash';
import TextField from '@mui/material/TextField';
import EquipmentList from '../../../components/equipment/EquipmentList';
import Box from '@mui/material/Box';
import { Tab, Tabs } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import MapIcon from '@mui/icons-material/Map';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';

type FilterTabType = 'search' | 'category' | 'location';

const EquipmentListPage: React.FC<RouteComponentProps<any>> = (
  props
): JSX.Element => {
  const classes = useEquipmentListStyles({});
  const { t } = useTranslation();
  const [activeFilterTab, setActiveFilterTab] = useState<FilterTabType>('search');
  const [category, setCategory] = useState('');
  const [searchValue, setSearchValue] = useState<string>('');
  const [selectedLocation, setSelectedLocation] = useState<string>('');

  const dispatch = useDispatch();
  const equipmentState: TEquipmentState = useSelector<
    IAppState,
    TEquipmentState
  >((state) => state.equipmentState);

  useEffect(() => {
    dispatch(getEquipmentCategories());
    dispatch(getEquipmentList());
  }, [dispatch]);

  const uniqueLocations = useMemo(() => {
    if (!equipmentState.data) return [];
    const locationSet = new Set<string>();
    equipmentState.data.forEach((item) => {
      item.locations?.forEach((loc: TApiEquipmentLocation) => {
        if (loc.title) locationSet.add(loc.title);
      });
    });
    return Array.from(locationSet).sort();
  }, [equipmentState.data]);

  const handleCategoryChange = (
    event: SelectChangeEvent<string>
  ) => {
    setCategory(event.target.value as string);
    dispatch(getEquipmentList(Number(event.target.value)));
  };

  const debouncedFilter = useMemo(
    () =>
      debounce((search: string, location?: string) => {
        dispatch(filterEquipmentList(search, location));
      }, 100),
    [dispatch]
  );

  const handleLocationChange = (event: SelectChangeEvent<string>) => {
    const newLocation = event.target.value as string;
    setSelectedLocation(newLocation);
    dispatch(filterEquipmentList(searchValue, newLocation));
  };

  return (
    <>
      <PageContent
        pageType={ContentPage.EQUIPMENT_PAGE}
        showAsParagraph={true}
      />

      <Box display="flex" flexDirection="column">
        <Tabs
          value={activeFilterTab}
          onChange={(_event, newValue) => setActiveFilterTab(newValue)}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab icon={<SearchIcon />} label={t('equipment.tab.filter')} value="search" />
          <Tab icon={<FormatListBulletedIcon />} label={t('equipment.tab.category')} value="category" />
          <Tab icon={<MapIcon />} label={t('equipment.tab.location')} value="location" />
        </Tabs>

        <Box mt={2}>
          {activeFilterTab === 'search' && (
            <TextField
              value={searchValue}
              label={t('equipment.search.label')}
              className={classes.searchField}
              onChange={(e) => {
                const val = e.target.value;
                setSearchValue(val);
                debouncedFilter(val, selectedLocation);
              }}
            />
          )}

          {activeFilterTab === 'category' && equipmentState.categories && (
            <EquipmentCategoryDropdown
              category={category}
              handleCategoryChange={handleCategoryChange}
              categories={equipmentState.categories}
            />
          )}

          {activeFilterTab === 'location' && (
            <FormControl variant="outlined" className={classes.formControl}>
              <InputLabel>{t('equipment.filter.location')}</InputLabel>
              <Select
                value={selectedLocation}
                onChange={handleLocationChange}
                label={t('equipment.filter.location')}
              >
                <MenuItem value="">
                  <em>{t('equipment.category.select.all')}</em>
                </MenuItem>
                {uniqueLocations.map((loc) => (
                  <MenuItem key={loc} value={loc}>
                    {loc}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
      </Box>

      <div className={classes.gridList}>
        <EquipmentList equipment={equipmentState.filteredList} />
      </div>
    </>
  );
};

export default withRouter(EquipmentListPage);
